<script setup>
/**
 * The full-page live-chat campaign builder (PRD F3/F4, variant B chrome +
 * variant A popovers): top bar with back/name/status/save-state/Schedule/
 * Publish, a real-size widget preview on a dotted canvas, and the
 * Content/Design/Targeting rail. Clicking any element in the preview opens
 * a floating popover editor anchored beside it — popovers and the rail
 * bind the same state, so they can never disagree.
 *
 * The page owns that single reactive form state hydrated from the store
 * campaign; edits autosave (2s debounce) through `campaigns/update`, and
 * Publish PATCHes `{ campaign_status: 'active', enabled: true }` — the
 * backend whitelists exactly the draft -> active transition.
 */
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useDebounceFn } from '@vueuse/core';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import Skeleton from 'dashboard/components-next/skeleton/Skeleton.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import BuilderTopBar from './BuilderTopBar.vue';
import BuilderPreview from './BuilderPreview.vue';
import BuilderPanel from './BuilderPanel.vue';
import BuilderPopover from './BuilderPopover.vue';
import ContentTab from './ContentTab.vue';
import DesignTab from './DesignTab.vue';
import TargetingTab from './TargetingTab.vue';
import CtaEditor from './CtaEditor.vue';
import ImageEditor from './ImageEditor.vue';
import {
  MAX_CTAS,
  applyCtaPatch,
  buildContentPayload,
  createCta,
  normalizeCampaignContent,
} from 'dashboard/components-next/Campaigns/helpers/campaignContent';
import {
  AVAILABILITIES,
  DEVICE_TYPES,
  FREQUENCIES,
  VISITOR_TYPES,
  WHERE,
  datetimeLocalToIso,
  epochToDatetimeLocal,
  testPageUrlFor,
  validateForPublish,
} from 'dashboard/components-next/Campaigns/helpers/campaignBuilder';
import {
  availabilityFromCampaign,
  whereFromCampaign,
} from 'dashboard/components-next/Campaigns/helpers/campaignConditions';
import {
  CAMPAIGN_CHANNEL_FILTERS,
  channelForCampaign,
} from 'dashboard/components-next/Campaigns/helpers/campaignFilters';
import { deriveCampaignStatus } from 'dashboard/components-next/Campaigns/helpers/campaignStatus';

const route = useRoute();
const router = useRouter();
const store = useStore();
const { t } = useI18n();

const campaigns = useMapGetter('campaigns/getAllCampaigns');

const displayId = computed(() => Number(route.params.displayId));
const campaign = computed(() =>
  campaigns.value.find(record => record.id === displayId.value)
);

const isLoading = ref(true);
const activeTab = ref('content');
const previewDevice = ref('desktop');

const state = reactive({
  title: '',
  headline: '',
  body: '',
  imageUrl: '',
  imageAlt: '',
  themeColor: '',
  ctas: [],
  inboxId: null,
  senderId: 0,
  where: WHERE.EVERYWHERE,
  url: '',
  timeOnPage: 10,
  visitorType: VISITOR_TYPES.ALL,
  deviceType: DEVICE_TYPES.ALL,
  frequency: FREQUENCIES.EVERY_VISIT,
  availability: AVAILABILITIES.ALWAYS,
  enabled: true,
  startsAt: '',
  endsAt: '',
});

// idle | saving | saved | error
const saveState = ref('idle');
const isPublishing = ref(false);
let retryTimer = null;

// Programmatic state writes (hydration, post-publish sync) must not trigger
// the autosave watcher. The flag flips back after the watcher's pre-flush
// pass has run and skipped.
let suppressAutosave = true;
const withAutosaveSuppressed = mutator => {
  suppressAutosave = true;
  mutator();
  nextTick(() => {
    suppressAutosave = false;
  });
};

const hydrateFromCampaign = record => {
  withAutosaveSuppressed(() => {
    const content = normalizeCampaignContent(record);
    Object.assign(state, {
      title: record.title ?? '',
      headline: content.headline,
      body: content.body,
      imageUrl: content.imageUrl,
      imageAlt: content.imageAlt,
      themeColor: content.themeColor,
      ctas: content.ctas.map(cta => ({ ...cta })),
      inboxId: record.inbox?.id ?? null,
      senderId: record.sender?.id ?? 0,
      where: whereFromCampaign(record),
      url: record.trigger_rules?.url ?? '',
      timeOnPage: record.trigger_rules?.time_on_page ?? 10,
      visitorType: record.trigger_rules?.visitor_type ?? VISITOR_TYPES.ALL,
      deviceType: record.trigger_rules?.device_type ?? DEVICE_TYPES.ALL,
      frequency: record.trigger_rules?.frequency ?? FREQUENCIES.EVERY_VISIT,
      availability: availabilityFromCampaign(record),
      enabled: Boolean(record.enabled),
      startsAt: epochToDatetimeLocal(record.starts_at),
      endsAt: epochToDatetimeLocal(record.ends_at),
    });
  });
};

const isDraft = computed(() => campaign.value?.campaign_status === 'draft');

const statusKey = computed(() => {
  if (!campaign.value || isDraft.value) return 'DRAFT';
  return deriveCampaignStatus({
    campaignStatus: campaign.value.campaign_status,
    campaignType: campaign.value.campaign_type,
    enabled: campaign.value.enabled,
    scheduledAt: campaign.value.scheduled_at,
  }).toUpperCase();
});

const previewContent = computed(() => ({
  headline: state.headline,
  body: state.body,
  imageUrl: state.imageUrl,
  imageAlt: state.imageAlt,
  themeColor: state.themeColor,
  ctas: state.ctas,
}));

// --- Click-to-edit popovers -------------------------------------------------
// The preview reports which element was clicked; the popover anchors to that
// element's DOM node and slots in the matching editor, bound to the same
// state the rail edits.

const previewRef = ref(null);
const activeEditor = ref(null); // { type: 'headline'|'body'|'image'|'cta', index? }
const editorAnchorEl = ref(null);

const popoverKey = computed(() =>
  activeEditor.value
    ? `${activeEditor.value.type}-${activeEditor.value.index ?? ''}`
    : ''
);

const popoverTitle = computed(() =>
  activeEditor.value
    ? t(`CAMPAIGN.BUILDER.POPOVER.${activeEditor.value.type.toUpperCase()}`)
    : ''
);

const activeCta = computed(() =>
  activeEditor.value?.type === 'cta'
    ? (state.ctas[activeEditor.value.index] ?? null)
    : null
);

const closeEditor = () => {
  activeEditor.value = null;
  editorAnchorEl.value = null;
};

const openEditor = async target => {
  activeEditor.value = target;
  await nextTick();
  editorAnchorEl.value = previewRef.value?.getAnchorEl(target) ?? null;
};

const addCtaFromPreview = () => {
  if (state.ctas.length >= MAX_CTAS) return;
  state.ctas = [...state.ctas, createCta()];
  openEditor({ type: 'cta', index: state.ctas.length - 1 });
};

const updateActiveCta = patch => {
  const { index } = activeEditor.value;
  state.ctas = state.ctas.map((cta, i) =>
    i === index ? applyCtaPatch(cta, patch) : cta
  );
};

const removeActiveCta = () => {
  const { index } = activeEditor.value;
  state.ctas = state.ctas.filter((_, i) => i !== index);
  closeEditor();
};

// Setting/clearing the image swaps the preview between the placeholder and
// the image element, unmounting the popover's anchor — re-grab the new one.
watch(
  () => Boolean(state.imageUrl),
  () => {
    if (activeEditor.value?.type === 'image') openEditor({ type: 'image' });
  }
);

// A CTA removed from the rail can strand an open CTA popover; the device
// toggle re-layouts the canvas under an anchored popover. Close in both.
watch(
  () => state.ctas.length,
  length => {
    if (
      activeEditor.value?.type === 'cta' &&
      activeEditor.value.index >= length
    )
      closeEditor();
  }
);
watch(previewDevice, closeEditor);

const buildPayload = () => ({
  title: state.title,
  // Keep the legacy `message` (widget fallback, card previews) projected
  // from the authored content.
  message: state.body?.trim() ? state.body : state.headline,
  content: buildContentPayload(state),
  inbox_id: state.inboxId,
  sender_id: state.senderId || null,
  enabled: state.enabled,
  // The legacy boolean stays in sync with `availability` so the widget keeps
  // honouring business hours while it still reads the old field.
  trigger_only_during_business_hours:
    state.availability === AVAILABILITIES.BUSINESS_HOURS,
  // An everywhere campaign omits `url` rather than sending '' — the model
  // validates any present url value, and gallery-created drafts start
  // without one.
  trigger_rules: {
    ...(state.where === WHERE.SPECIFIC_URL && state.url
      ? { url: state.url }
      : {}),
    time_on_page: state.timeOnPage,
    visitor_type: state.visitorType,
    device_type: state.deviceType,
    frequency: state.frequency,
    availability: state.availability,
  },
  starts_at: datetimeLocalToIso(state.startsAt),
  ends_at: datetimeLocalToIso(state.endsAt),
});

// The model requires title, inbox and message presence — a payload missing
// them would 422 on every keystroke, so autosave waits until they exist.
const canAutosave = computed(() =>
  Boolean(
    state.title.trim() &&
      state.inboxId &&
      (state.body?.trim() || state.headline.trim())
  )
);

// A 422 means the content itself is invalid (an unreachable CTA url, a
// malformed phone) — retrying sends the exact same payload and fails forever,
// silently dropping every later edit. Only transient failures are retried, and
// only a few times; anything else parks in `unsaved` and tells the author.
const MAX_SAVE_RETRIES = 3;
let saveRetries = 0;

const performSave = async () => {
  if (!canAutosave.value || isPublishing.value) return;

  saveState.value = 'saving';
  try {
    await store.dispatch('campaigns/update', {
      id: displayId.value,
      ...buildPayload(),
    });
    saveState.value = 'saved';
    saveRetries = 0;
  } catch (error) {
    const status = error?.response?.status;
    const isTransient = !status || status >= 500;

    if (isTransient && saveRetries < MAX_SAVE_RETRIES) {
      saveRetries += 1;
      saveState.value = 'error';
      retryTimer = setTimeout(performSave, 5000);
      return;
    }

    saveRetries = 0;
    saveState.value = 'unsaved';
    useAlert(
      error?.response?.data?.message || t('CAMPAIGN.BUILDER.SAVE_ERROR_MESSAGE')
    );
  }
};

const debouncedSave = useDebounceFn(performSave, 2000);

watch(
  state,
  () => {
    if (suppressAutosave) return;
    debouncedSave();
  },
  { deep: true }
);

// Opens the page the campaign targets so the author can watch it fire for
// real. `noopener` because the test page is arbitrary customer-supplied URL.
const openTestPage = () => {
  window.open(testPageUrlFor(campaign.value), '_blank', 'noopener');
};

const goBack = () => {
  router.push({
    name: 'campaigns_index',
    params: { accountId: route.params.accountId },
  });
};

const leaveWithAlert = message => {
  useAlert(message);
  router.replace({
    name: 'campaigns_index',
    params: { accountId: route.params.accountId },
  });
};

const PUBLISH_ERROR_TABS = {
  CONTENT: 'content',
  CTAS: 'content',
  INBOX: 'targeting',
  URL: 'targeting',
  TIME_ON_PAGE: 'targeting',
};

const handlePublish = async () => {
  const errors = validateForPublish(state);
  if (errors.length) {
    const [firstError] = errors;
    activeTab.value = PUBLISH_ERROR_TABS[firstError];
    useAlert(t(`CAMPAIGN.BUILDER.PUBLISH_ERRORS.${firstError}`));
    return;
  }

  isPublishing.value = true;
  try {
    await store.dispatch('campaigns/update', {
      id: displayId.value,
      ...buildPayload(),
      enabled: true,
      campaign_status: 'active',
    });
    withAutosaveSuppressed(() => {
      state.enabled = true;
    });
    useAlert(t('CAMPAIGN.BUILDER.PUBLISHED_MESSAGE'));
  } catch (error) {
    useAlert(t('CAMPAIGN.BUILDER.PUBLISH_ERROR_MESSAGE'));
  } finally {
    isPublishing.value = false;
  }
};

onMounted(async () => {
  if (!campaign.value) {
    await store.dispatch('campaigns/get');
  }

  if (!campaign.value) {
    leaveWithAlert(t('CAMPAIGN.BUILDER.NOT_FOUND'));
    return;
  }

  if (
    channelForCampaign(campaign.value) !== CAMPAIGN_CHANNEL_FILTERS.LIVE_CHAT
  ) {
    leaveWithAlert(t('CAMPAIGN.BUILDER.NOT_LIVE_CHAT'));
    return;
  }

  hydrateFromCampaign(campaign.value);
  isLoading.value = false;
});

onBeforeUnmount(() => {
  if (retryTimer) clearTimeout(retryTimer);
});
</script>

<template>
  <section class="flex flex-col w-full h-full overflow-hidden bg-n-surface-1">
    <div v-if="isLoading" class="flex flex-col gap-4 p-6">
      <Skeleton class="h-10" announce />
      <Skeleton class="h-64" />
    </div>

    <template v-else>
      <BuilderTopBar
        v-model:title="state.title"
        :status-key="statusKey"
        :save-state="saveState"
        :can-publish="isDraft"
        :is-publishing="isPublishing"
        @back="goBack"
        @test="openTestPage"
        @schedule="activeTab = 'design'"
        @publish="handlePublish"
      />

      <div class="flex flex-1 min-h-0">
        <BuilderPreview
          ref="previewRef"
          v-model:device="previewDevice"
          :content="previewContent"
          :active-target="activeEditor"
          @edit="openEditor"
          @add-cta="addCtaFromPreview"
        />

        <BuilderPanel v-model:active-tab="activeTab">
          <template #content>
            <ContentTab
              v-model:headline="state.headline"
              v-model:body="state.body"
              v-model:ctas="state.ctas"
            />
          </template>

          <template #design>
            <DesignTab
              v-model:theme-color="state.themeColor"
              v-model:image-url="state.imageUrl"
              v-model:image-alt="state.imageAlt"
              v-model:enabled="state.enabled"
              v-model:starts-at="state.startsAt"
              v-model:ends-at="state.endsAt"
            />
          </template>

          <template #targeting>
            <TargetingTab
              v-model:inbox-id="state.inboxId"
              v-model:sender-id="state.senderId"
              v-model:where="state.where"
              v-model:url="state.url"
              v-model:time-on-page="state.timeOnPage"
              v-model:visitor-type="state.visitorType"
              v-model:device-type="state.deviceType"
              v-model:frequency="state.frequency"
              v-model:availability="state.availability"
            />
          </template>
        </BuilderPanel>
      </div>

      <BuilderPopover
        v-if="activeEditor && editorAnchorEl"
        :key="popoverKey"
        :anchor-el="editorAnchorEl"
        :title="popoverTitle"
        @close="closeEditor"
      >
        <Input
          v-if="activeEditor.type === 'headline'"
          v-model="state.headline"
          :label="t('CAMPAIGN.BUILDER.CONTENT.HEADLINE.LABEL')"
          :placeholder="t('CAMPAIGN.BUILDER.CONTENT.HEADLINE.PLACEHOLDER')"
        />

        <TextArea
          v-else-if="activeEditor.type === 'body'"
          v-model="state.body"
          auto-height
          :max-length="500"
          :label="t('CAMPAIGN.BUILDER.CONTENT.BODY.LABEL')"
          :placeholder="t('CAMPAIGN.BUILDER.CONTENT.BODY.PLACEHOLDER')"
        />

        <ImageEditor
          v-else-if="activeEditor.type === 'image'"
          v-model:image-url="state.imageUrl"
          v-model:image-alt="state.imageAlt"
        />

        <CtaEditor
          v-else-if="activeCta"
          :cta="activeCta"
          @update="updateActiveCta"
          @remove="removeActiveCta"
        />
      </BuilderPopover>
    </template>
  </section>
</template>
